// Evening recap — daily "did you check off your habits?" flow.
//
// At the user's configured time (default 9:30 PM local), iOS fires a local
// notification. When the user opens the app any time after that today, the
// dashboard auto-opens a recap modal that lets them confirm their habit
// check-ins by speaking ("I went to the gym and read, didn't run") instead
// of tapping each cell.
//
// State lives in localStorage:
//   proxera.recap.prefs.v1   { enabled: bool, time: 'HH:MM' }
//   proxera.recap.shown.v1   ISO date 'YYYY-MM-DD' of the last day we
//                            opened the recap modal (whether or not the
//                            user actually saved). Used to gate the
//                            auto-open so a single check-in cleanup
//                            doesn't re-fire every time the dashboard
//                            mounts.
//
// We schedule the iOS-side notification via @capacitor/local-notifications
// whenever prefs change. The same plugin's permission prompt fires the
// first time the user enables the feature; if denied, we keep the in-app
// modal working (it's the source of truth) — they just won't get the OS
// nudge.

// Note: @capacitor/local-notifications is only loaded when running on the
// iOS shell (see `loadLocalNotifications` below). On Electron / web we no-op
// and rely on the in-app modal — same UX outcome, just no OS-level nudge.
import { isIOS } from './platform.js';

let _localNotificationsCache;
async function loadLocalNotifications() {
  if (!isIOS()) return null;
  if (_localNotificationsCache) return _localNotificationsCache;
  try {
    // Variable-indirect the specifier so Vite's dev import analyzer skips it.
    const lnMod = '@capacitor/local-notifications';
    const mod = await import(/* @vite-ignore */ lnMod);
    _localNotificationsCache = mod.LocalNotifications;
    return _localNotificationsCache;
  } catch {
    return null;
  }
}

const PREFS_KEY = 'proxera.recap.prefs.v1';
const SHOWN_KEY = 'proxera.recap.shown.v1';
const NOTIFICATION_ID = 1001;

const DEFAULTS = { enabled: true, time: '21:30' };

// Pub/sub for prefs so the Profile settings UI can re-render reactively.
const listeners = new Set();
function emit() { for (const fn of listeners) { try { fn(); } catch {} } }

export function subscribePrefs(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Reads ──────────────────────────────────────────────────────────
// Cached at module scope — `useSyncExternalStore` requires getSnapshot
// to return a STABLE reference between renders when nothing changed.
// Parsing localStorage on every call returned a fresh object each time
// and React threw / bailed out (the same trap aiConsent.js hits).
let cachedPrefs = readFromStorage();

function readFromStorage() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULTS.enabled,
      time: typeof parsed.time === 'string' && /^\d{2}:\d{2}$/.test(parsed.time) ? parsed.time : DEFAULTS.time,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function getPrefs() {
  return cachedPrefs;
}

function writePrefs(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {}
  cachedPrefs = { ...prefs };
  emit();
}

// Cross-tab / cross-context sync: if localStorage is mutated outside
// this module, refresh the cache so the next read picks up the change
// AND fires subscribers (so any open Profile UI re-renders).
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (e) => {
    if (e.key === PREFS_KEY || e.key === null) {
      cachedPrefs = readFromStorage();
      emit();
    }
  });
}

// "Shown today" tracking — keyed by the user's local calendar date.
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function wasShownToday() {
  try { return localStorage.getItem(SHOWN_KEY) === todayKey(); } catch { return false; }
}

export function markShownToday() {
  try { localStorage.setItem(SHOWN_KEY, todayKey()); } catch {}
}

// ── Time math ──────────────────────────────────────────────────────
// Parse "HH:MM" → { hours, minutes } (24-hour).
export function parseTime(s) {
  const m = /^(\d{2}):(\d{2})$/.exec(s || '');
  if (!m) return { hours: 21, minutes: 30 };
  return { hours: clamp(+m[1], 0, 23), minutes: clamp(+m[2], 0, 59) };
}
function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

// Has the configured time already passed today, in local time?
function isPastRecapTimeToday(prefs) {
  const { hours, minutes } = parseTime(prefs.time);
  const now = new Date();
  return (now.getHours() > hours) || (now.getHours() === hours && now.getMinutes() >= minutes);
}

// Format "HH:MM" 24-hour into "9:30 PM" 12-hour for display.
export function formatTime12(s) {
  const { hours, minutes } = parseTime(s);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = ((hours % 12) || 12);
  return `${h12}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

// ── Should we open the modal right now? ────────────────────────────
//
// Inputs:
//   habitsToday  — array of habits scheduled for today
//   doneCountToday — how many are already checked off
//
// Rules:
//   - Feature must be enabled
//   - It must be at or past the configured time
//   - We must not have already shown the modal today
//   - There must be at least one habit scheduled today
//   - At least one habit must NOT yet be done (don't bug users who
//     already finished everything manually)
export function shouldShowToday({ habitsToday = [], doneCountToday = 0 } = {}) {
  const prefs = getPrefs();
  if (!prefs.enabled) return false;
  if (wasShownToday()) return false;
  if (!isPastRecapTimeToday(prefs)) return false;
  if (habitsToday.length === 0) return false;
  if (doneCountToday >= habitsToday.length) return false;
  return true;
}

// ── Native notification scheduling ─────────────────────────────────
//
// Capacitor's LocalNotifications plugin lets us schedule an absolute date
// + a daily repeat. iOS only wakes the app for repeating notifications
// after the FIRST occurrence has been scheduled with a future date, so
// we compute the next occurrence (today if still upcoming, otherwise
// tomorrow) before passing `at`.
//
// Permissions: we silently no-op on web (the plugin throws there) and we
// prompt the user the first time we enable on native. If they deny we
// still keep the in-app modal working — the OS notification is just a
// nudge.

function nextOccurrence(prefs) {
  const { hours, minutes } = parseTime(prefs.time);
  const next = new Date();
  next.setHours(hours, minutes, 0, 0);
  if (next <= new Date()) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

// Local convenience wrapper. `isIOS()` from platform.js is the runtime check;
// we also need access to the LocalNotifications plugin instance, which we
// load lazily so the desktop bundle doesn't try to resolve the iOS plugin
// at build time.

export async function ensureNotificationPermission() {
  const LN = await loadLocalNotifications();
  if (!LN) return false;
  try {
    const status = await LN.checkPermissions();
    if (status.display === 'granted') return true;
    const req = await LN.requestPermissions();
    return req.display === 'granted';
  } catch {
    return false;
  }
}

export async function rescheduleNotification() {
  const LN = await loadLocalNotifications();
  if (!LN) return;
  const prefs = getPrefs();

  // Tear down the prior schedule unconditionally so a disable or a time
  // change leaves no orphaned notification.
  try {
    await LN.cancel({ notifications: [{ id: NOTIFICATION_ID }] });
  } catch { /* nothing scheduled — fine */ }

  if (!prefs.enabled) return;

  const granted = await ensureNotificationPermission();
  if (!granted) return;

  try {
    await LN.schedule({
      notifications: [{
        id: NOTIFICATION_ID,
        title: 'Did you finish today?',
        body: 'Tap to recap your habits for today. Speak or tap them in.',
        schedule: {
          at: nextOccurrence(prefs),
          repeats: true,
          allowWhileIdle: true,
          // `every: 'day'` plus `at` gives us "fire at this time every day."
          every: 'day',
        },
      }],
    });
  } catch (err) {
    // Failure here is non-fatal — the in-app modal still works. Log so
    // the user can debug if they don't see the notification.
    // eslint-disable-next-line no-console
    console.warn('[eveningRecap] failed to schedule notification', err);
  }
}

// Setters — also reschedule the notification so the OS-side stays in sync.

export function setEnabled(enabled) {
  const prefs = getPrefs();
  writePrefs({ ...prefs, enabled: !!enabled });
  rescheduleNotification();
}

export function setTime(time) {
  if (!/^\d{2}:\d{2}$/.test(time || '')) return;
  const prefs = getPrefs();
  writePrefs({ ...prefs, time });
  rescheduleNotification();
}

// Called once on app boot so the notification gets re-scheduled if iOS
// dropped it (e.g. fresh install of a new build wiped the schedule).
export async function bootSync() {
  await rescheduleNotification();
}
